// @ts-ignore: Paraglide is vanilla js
import * as m from '@klaviyo-mkt/i18n/paraglide/messages';
import type { IAlertBarData } from '@klaviyo-mkt/components/molecules/alert-bar/types';
import type { ILastViewedResource, ILastViewedCollection } from '@academy/skilljar/stores/last-viewed-resource';

const getLoggedOutAlertBar = (locale: string): IAlertBarData => ({
  backgroundColor: 'poppy',
  enabled: true,
  href: `/${locale}/login?next=%2F${locale}`,
  message: `<span class="bold">${m.alertBar_partnerAcademy_loggedOut()}</span>`,
  openInNewTab: false,
  showIcon: false,
  textColor: 'white',
});

const createLoginUrl = (hasClientRendered: boolean, locale: string, path?: string): string => {
  const currentPath = hasClientRendered
    ? window.location.pathname
    : path || `/${locale}`;
  return `/${locale}/login?next=${encodeURIComponent(currentPath)}`;
};

const isHomepage = (hasClientRendered: boolean, locale: string, path?: string): boolean => {
  return hasClientRendered
    ? window.location.pathname.endsWith('/') || window.location.pathname.endsWith(`/${locale}`)
    : path === `/${locale}` || path === '/';
};

const shouldHideDuplicateContent = (
  lastViewedResource: ILastViewedResource | null,
  lastViewedCollection: ILastViewedCollection | null
): boolean => {
  if (!lastViewedResource) return false;

  const progressCtaResource = lastViewedCollection || lastViewedResource;
  return !!(progressCtaResource &&
    lastViewedResource.id === progressCtaResource.id &&
    lastViewedResource.name === progressCtaResource.name);
};

export const getAlertBarConfig = ({
  hasClientRendered,
  locale,
  loggedIn,
  onResourcePage,
  lastViewedResource,
  lastViewedCollection,
  path
}: {
  hasClientRendered: boolean;
  locale?: string;
  loggedIn: boolean | null;
  onResourcePage: boolean;
  lastViewedResource: ILastViewedResource | null;
  lastViewedCollection: ILastViewedCollection | null;
  path?: string;
}): IAlertBarData | undefined => {
  const normalizedLocale = locale?.toLowerCase() || 'en-us';
  const loginUrl = createLoginUrl(hasClientRendered, normalizedLocale, path);

  if (!hasClientRendered) {
    return {
      ...getLoggedOutAlertBar(normalizedLocale),
      href: loginUrl,
    };
  }

  if (!loggedIn) {
    return {
      ...getLoggedOutAlertBar(normalizedLocale),
      href: loginUrl,
    };
  }

  if (onResourcePage) {
    return undefined;
  }

  if (!lastViewedResource) {
    return undefined;
  }

  if (isHomepage(hasClientRendered, normalizedLocale, path) &&
      shouldHideDuplicateContent(lastViewedResource, lastViewedCollection)) {
    return undefined;
  }

  return {
    backgroundColor: 'poppy',
    enabled: true,
    href: lastViewedResource.link,
    message: `<span class="bold">${m.alertBar_partnerAcademy_loggedIn()}: ${lastViewedResource.name}</span>`,
    openInNewTab: false,
    showIcon: false,
    textColor: 'white',
  };
};
